import { z } from "zod";

export const createTimesheetSchema = (customFields = []) => z
  .object({
    timesheet_date: z.preprocess(
      (val) => {
        if (val instanceof Date && !isNaN(val)) return val;
        if (typeof val === "string" && val.trim() !== "") {
          const parsed = new Date(val);
          return isNaN(parsed) ? undefined : parsed;
        }
        return undefined;
      },
      z.date({
        required_error: "Date is required",
        invalid_type_error: "Invalid date",
      })
    ),
    timesheet_day: z.preprocess(
      (val) => (val === "" ? undefined : val),
      z.enum(["WD", "WO", "HD"], {
        required_error: "Day is required",
        invalid_type_error: "Day is required",
      })
    ),
    shift_type: z.preprocess(
      (val) => (val === null ? undefined : val),
      z
        .number({
          required_error: "Shift Type is required",
          invalid_type_error: "Shift Type is required",
        })
        .min(1, "Shift Type is required")
    ),
    project: z.preprocess(
      (val) => (val === null ? undefined : val),
      z.number({
        required_error: "Project is required",
        invalid_type_error: "Project is required",
      })
    ),
    task: z.preprocess(
      (val) => (val === null ? undefined : val),
      z.any()
    ),
    entry_type: z.enum(["1", "2"], {
      required_error: "Entry type is required",
      invalid_type_error: "Entry type is required",
    }),
    timesheet_hours: z.preprocess(
      (val) => (val instanceof Date ? val : undefined),
      z
        .date({
          required_error: "Hours is required",
          invalid_type_error: "Invalid time",
        })
        .refine((date) => !(date.getHours() === 0 && date.getMinutes() === 0),
          { message: "Hours cannot be 00:00" }
        )
    ),
    start_time: z
      .preprocess(
        (val) => (val instanceof Date ? val : undefined),
        z.date({
          required_error: "Start time is required",
          invalid_type_error: "Invalid time",
        })
      )
      .optional(),
    end_time: z
      .preprocess(
        (val) => (val instanceof Date ? val : undefined),
        z.date({
          required_error: "End time is required",
          invalid_type_error: "Invalid time",
        })
      )
      .optional(),
    comp_off: z.boolean().optional(),
    description: z.preprocess(
      (val) => (typeof val === "string" ? val.trim() : val),
      z.string().min(1, "Description is required")
    ),

    custom_fields: z.record(z.string(), z.any()).optional(),
  })
  .superRefine((data, ctx) => {
    if (
      !(
        (data.entry_type === "1" && !!data.timesheet_hours) ||
        (data.entry_type === "2" && !!data.start_time && !!data.end_time)
      )
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Please provide valid time entries",
        path: ["entry_type"],
      });
    }

    customFields.forEach((cf) => {
      if (!cf.is_required) return;

      const value = data.custom_fields?.[cf.field_key];
      const isEmpty =
        value === undefined ||
        value === null ||
        value === "" ||
        (cf.field_type === "checkbox" && value === false);

      if (isEmpty) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `${cf.field_label} is required`,
          path: ["custom_fields", cf.field_key],
        });
      }

      if (cf.max_words && typeof value === "string" && value.length > cf.max_words) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `${cf.field_label} must not exceed ${cf.max_words} characters`,
          path: ["custom_fields", cf.field_key],
        });
      }
    });
  });

